<template>
  <div>
    <div class="setup-group-head"><span>Required setup</span><span class="line"></span></div>

    <!-- Word count -->
    <setup-collapse title="Word count" :summary="wordCountSummary" required done default-open>
      <div class="field-grid-2">
        <div>
          <span class="field-label">Word count</span>
          <div class="help-text" style="margin-bottom: 10px;">Minimum word count you can select for a generation task is {{ MIN_WORD_COUNT }} words.</div>
          <div class="radio-list">
            <label class="radio-row" :class="{ selected: model.word_count_mode === 'current' }" @click="setWordMode('current')">
              <span class="radio-dot"></span>
              <span>Current word count ({{ currentWordCount || 0 }})</span>
            </label>
            <label class="radio-row" :class="{ selected: model.word_count_mode === 'recommended' }" @click="setWordMode('recommended')">
              <span class="radio-dot"></span>
              <span>Target word count ({{ targetWordCount || 0 }})</span>
              <span class="recommend-badge">Recommended</span>
            </label>
            <label class="radio-row" :class="{ selected: model.word_count_mode === 'custom' }" @click="setWordMode('custom')">
              <span class="radio-dot"></span>
              <span>Custom word count</span>
            </label>
          </div>
          <div v-if="model.word_count_mode === 'custom'" class="wc-custom">
            <span class="wc-custom-label"># of words</span>
            <input type="number" class="wc-custom-input" :min="MIN_WORD_COUNT" step="50" :value="model.word_count" @input="setCustomWordCount($event.target.value)" />
          </div>
        </div>
        <div class="info-banner">
          <div class="info-banner-title"><span class="ico">i</span> Word count for silo pages</div>
          For top-level pages, it is best to stick to POP's target word count recommendation, while for supporting pages word count is not strictly defined and the best practice is to have around 500-1,000 words.
          <a class="info-banner-link" href="#">Learn more about silo pages
            <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>
          </a>
        </div>
      </div>
    </setup-collapse>

    <!-- Competitors -->
    <setup-collapse title="Competitors" :summary="competitorsSummary" required :done="competitorsDone">
      <span class="field-label">Your competitors</span>
      <div class="help-text" style="margin-bottom: 12px;">Select up to 3 competitors. POP will use their content for inspiration without copying it.</div>
      <table class="comp-table">
        <thead>
          <tr>
            <th style="width: 40px;">Select</th>
            <th>URL / Title</th>
            <th style="width: 110px; text-align: right;">Word count</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(c, idx) in model.competitors" :key="idx" :class="{ 'custom-row': c.source === 'custom' }">
            <td><button class="toggle" :class="{ on: c.enabled }" @click="toggleCompetitor(c)"></button></td>
            <td class="url-cell">
              <a class="url-link" :href="c.url" target="_blank" rel="noopener">{{ c.url }}</a>
              <span v-if="c.source === 'custom'" class="url-badge">Custom</span>
              <button v-if="c.source === 'custom'" class="url-remove" title="Remove" @click="removeCompetitor(idx)">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
              </button>
              <div v-if="c.title" class="url-title">{{ c.title }}</div>
            </td>
            <td class="word-cell">{{ c.word_count || '-' }}</td>
          </tr>
          <tr v-if="!model.competitors.length">
            <td colspan="3" class="help-text" style="text-align: center; padding: 16px;">No competitors from the report. Add a custom URL below.</td>
          </tr>
        </tbody>
      </table>

      <div v-if="!customForm.show" class="comp-table-foot">
        <span>Not seeing the right pages? Paste a competitor URL POP didn't find.</span>
        <button class="aiw2-btn aiw2-btn--soft aiw2-btn--xs" style="margin-left: auto;" @click="openCustomForm">+ Add custom URL</button>
      </div>

      <div v-else class="custom-url-form">
        <div class="custom-url-form-label"><span class="dot"></span> Add a custom competitor URL</div>
        <div class="custom-url-input-row">
          <label class="custom-url-field">
            <svg class="link-ico" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/></svg>
            <input
              ref="customInput"
              type="url"
              class="text-input custom-url-input"
              :class="{ invalid: !!customForm.error }"
              v-model="customForm.url"
              placeholder="https://example.com/competitor-article"
              autocomplete="off"
              spellcheck="false"
              @input="customForm.error = ''"
              @keydown.enter.prevent="addCustomUrl"
              @keydown.esc.prevent="closeCustomForm"
            />
          </label>
          <button class="aiw2-btn aiw2-btn--primary" :disabled="!isValidUrl(customForm.url)" @click="addCustomUrl">Add URL</button>
          <button class="aiw2-btn aiw2-btn--ghost" @click="closeCustomForm">Cancel</button>
        </div>
        <div class="custom-url-hint" :class="{ 'is-error': !!customForm.error }">{{ customForm.error || "POP will fetch this page and use its content for inspiration - without copying it." }}</div>
      </div>
    </setup-collapse>

    <!-- Web page type -->
    <setup-collapse title="Web page type" :summary="pageTypeLabel" required done>
      <span class="field-label">Select web page type</span>
      <div class="help-text" style="margin-bottom: 12px;">This changes the structure, tone, rich content placement, and SEO strategy.</div>
      <div class="page-type-grid">
        <button
          v-for="pt in PAGE_TYPES"
          :key="pt.value"
          class="pt-card"
          :class="{ selected: model.page_type === pt.value }"
          @click="selectPageType(pt.value)"
        >
          <div class="pt-name">{{ pt.label }}</div>
          <div class="pt-desc">{{ pt.description }}</div>
        </button>
      </div>
    </setup-collapse>
  </div>
</template>

<script>
import SetupCollapse from "./SetupCollapse.vue";
import { PAGE_TYPES, MIN_WORD_COUNT } from "../../constants";

export default {
  name: "AiwV2RequiredSetupSection",
  components: { SetupCollapse },
  props: {
    model: { type: Object, required: true },
    currentWordCount: { type: Number, default: 0 },
    targetWordCount: { type: Number, default: 0 },
  },
  data() {
    return { PAGE_TYPES, MIN_WORD_COUNT, customForm: { show: false, url: "", error: "" } };
  },
  computed: {
    wordCountSummary() {
      if (this.model.word_count_mode === "current") return `Current word count (${this.currentWordCount || 0} words)`;
      if (this.model.word_count_mode === "recommended") return `Target word count (${this.targetWordCount || 0} words)`;
      return `Custom (${this.model.word_count || 0} words)`;
    },
    competitorsSummary() {
      const total = this.model.competitors.length;
      const on = this.model.competitors.filter((c) => c.enabled).length;
      return total ? `${on} of ${total} selected · sorted by relevance` : "None selected";
    },
    selectedCompetitors() {
      return this.model.competitors.filter((c) => c.enabled).length;
    },
    competitorsDone() {
      // 1–3 competitors must be selected to continue.
      return this.selectedCompetitors >= 1 && this.selectedCompetitors <= 3;
    },
    pageTypeLabel() {
      const pt = PAGE_TYPES.find((p) => p.value === this.model.page_type);
      return pt ? pt.label : "";
    },
  },
  methods: {
    emit() {
      this.$emit("change");
    },
    setWordMode(mode) {
      this.model.word_count_mode = mode;
      if (mode === "current") this.model.word_count = this.currentWordCount || 0;
      else if (mode === "recommended") this.model.word_count = this.targetWordCount || 0;
      this.emit();
    },
    setCustomWordCount(value) {
      this.model.word_count = Math.max(0, parseInt(value, 10) || 0);
      this.emit();
    },
    toggleCompetitor(c) {
      // Enforce "up to 3": block enabling a 4th.
      if (!c.enabled && this.selectedCompetitors >= 3) return;
      c.enabled = !c.enabled;
      this.emit();
    },
    openCustomForm() {
      this.customForm = { show: true, url: "", error: "" };
      this.$nextTick(() => this.$refs.customInput && this.$refs.customInput.focus());
    },
    closeCustomForm() {
      this.customForm = { show: false, url: "", error: "" };
    },
    normalizeUrl(value) {
      let v = (value || "").trim();
      if (!v) return "";
      if (!/^https?:\/\//i.test(v)) v = "https://" + v;
      return v;
    },
    isValidUrl(value) {
      try {
        const u = new URL(this.normalizeUrl(value));
        return !!u.hostname && u.hostname.indexOf(".") !== -1;
      } catch (e) {
        return false;
      }
    },
    titleFromUrl(url) {
      try {
        const u = new URL(url);
        const seg = u.pathname.split("/").filter(Boolean).pop() || u.hostname;
        const words = seg.replace(/\.(html?|php|aspx?)$/i, "").replace(/[-_]+/g, " ").trim();
        if (!words) return "Custom competitor page";
        return words.charAt(0).toUpperCase() + words.slice(1);
      } catch (e) {
        return "Custom competitor page";
      }
    },
    addCustomUrl() {
      const raw = this.normalizeUrl(this.customForm.url);
      if (!this.isValidUrl(raw)) {
        this.customForm.error = "That doesn't look like a valid URL. Try something like https://example.com/article";
        return;
      }
      const exists = this.model.competitors.some((c) => (c.url || "").trim() === raw);
      if (exists) {
        this.customForm.error = "That URL is already in your competitor list.";
        return;
      }
      // Auto-toggle the new custom competitor ON (per spec). If this pushes the
      // selection past 3, the shared 1–3 gate blocks Continue until the user
      // deselects one — no separate cap here.
      this.model.competitors.push({
        url: raw, enabled: true, source: "custom", title: this.titleFromUrl(raw), word_count: 0,
      });
      this.emit();
      this.closeCustomForm();
    },
    removeCompetitor(idx) {
      this.model.competitors.splice(idx, 1);
      this.emit();
    },
    selectPageType(value) {
      this.model.page_type = value;
      this.emit();
    },
  },
};
</script>
